import { isNativeContainer } from './index';

/**
 * Messages the platform sends out to the native container.
 *
 * The container needs the signed-in user's id so it can register this device's
 * push token against the right account (SRS FR-15, FR-29), and needs to know
 * about logout so it can deregister that token (FR-13). The platform is the
 * only side that knows either, because it owns the Auth0 session.
 */
export type NativeMessage =
  | { type: 'USER_IDENTIFIED'; userId: number }
  | { type: 'USER_SIGNED_OUT' };

type ReactNativeWebViewBridge = { postMessage: (payload: string) => void };

declare global {
  interface Window {
    ReactNativeWebView?: ReactNativeWebViewBridge;
  }
}

/**
 * Send a message to the container. A no-op in a normal browser, and a no-op if
 * the container is an older build that has not injected its bridge yet, so
 * calling this unconditionally from shared code is safe.
 *
 * `window.ReactNativeWebView` is the only channel, which is what
 * react-native-webview injects on both iOS and Android. A raw WKWebView would
 * use `webkit.messageHandlers` instead and every message here would be
 * dropped; the container is react-native-webview on both platforms, so that
 * case cannot arise without a rewrite of the app's shell.
 *
 * Returns whether the message actually left, so a caller that must not lose one
 * can retry rather than assume it landed.
 */
export function postToNative(message: NativeMessage): boolean {
  if (!isNativeContainer()) return false;

  const bridge = window.ReactNativeWebView;
  if (!bridge) return false;

  try {
    bridge.postMessage(JSON.stringify(message));
    return true;
  } catch {
    // The bridge is best-effort. A failure here must never break the platform,
    // which has to keep working identically in a normal browser.
    return false;
  }
}
